import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dropdown,
  IconButton,
  LinearProgress,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/joy';
import type { IconType } from 'react-icons';
import { FaCheck, FaEllipsisV, FaPaperPlane, FaPlus } from 'react-icons/fa';
import { MdOutlineDescription, MdReceiptLong, MdRequestQuote } from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import type { Document } from '../../modules/docs/types/documents.types';
import {
  balanceReissueAllowed,
  documentDisplayRef,
  invoiceDisplayStatus,
  invoiceRoleLabel,
  normalizeInvoiceRole,
  sortInvoicesForSequence,
  totalBalanceDeductions,
  type BalanceAdjustment,
  type InvoiceRole,
} from '../../modules/docs/utils/invoiceLedger';
import { BalanceDeductionsModal } from './BalanceDeductionsModal';
import { getAvailableStatuses, type DocumentStatus } from '../../modules/docs/types/documents.types';
import { documentIdsWithCustomerTransmissions } from '../../modules/docs/services/documentShareLinks.service';
import { invoiceDeleteAllowedWithSchedule } from './paymentSchedule';
import { reconcileInvoices, type InvoiceReconciliation } from './invoiceReconciliation';
import type { PaymentScheduleStage, PresentedScheduleStage } from './paymentSchedule.types';
import {
  dateOnlyToIssuedAt,
  formatDateGb,
  invoicePaymentScheduleLocked,
  invoicePaymentTermsEditable,
  resolveInvoicePaymentSchedule,
} from '../../modules/docs/utils/invoicePaymentTerms';
import { InvoicePaymentTermsModal } from '../../modules/docs/components/InvoicePaymentTermsModal';
import { documentsService } from '../../modules/docs/services/documents.service';
import {
  getSyncStatus,
  resyncInvoiceToFreeAgent,
  type SyncStatus,
} from '../../integrations/freeagentSync';
import { isAuthenticated, initiateAuthorization } from '../../freeagent/auth';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';

function formatMoney(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value));
}

function stepIcon(title: string, role?: InvoiceRole | null): IconType {
  if (role === 'deposit' || title.toLowerCase().includes('deposit')) return MdReceiptLong;
  if (role === 'variation' || title.toLowerCase().includes('variation')) return MdOutlineDescription;
  return MdRequestQuote;
}

function invoiceStatusMeta(invoice: Document, customerTransmitted?: boolean): {
  label: string;
  color: 'success' | 'primary' | 'neutral' | 'danger';
  icon?: IconType;
} {
  const meta = invoiceDisplayStatus({
    status: invoice.status,
    customerTransmitted,
  });
  return {
    ...meta,
    icon: meta.label === 'Sent' ? FaPaperPlane : (meta.color === 'success' ? FaCheck : undefined),
  };
}

type SyncInfo = {
  syncStatus: SyncStatus;
  freeAgentUrl?: string;
  lastSyncedAt?: string;
  syncError?: string;
};

type SequenceStep = {
  key: string;
  number: number;
  title: string;
  body: string;
  icon: IconType;
  invoice: Document | null;
  previewAmount?: number | null;
  previewCaption?: string;
  readyAction?: { label: string; onClick: () => void; disabled?: boolean; loading?: boolean };
};

export function InvoiceSequencePanel({
  invoices,
  finalSettled,
  nextRole,
  creatingInvoice,
  firstAccepted,
  quoteHasDeposit,
  selectedId,
  onSelect,
  onView,
  onEdit,
  onExportPdf,
  onDelete,
  onReissueBalance,
  onSaveDeductions,
  onCreateCredit,
  finalBalanceIssued = false,
  balanceAdjustments = {},
  onIssueBalance,
  onCreateDeposit,
  onChangeStatus,
  statusBusyId,
  onDocumentsChanged,
  anticipatedBalance,
  agreedQuoteTotal,
  scheduleStages,
  scheduleRawStages,
  plainList = false,
}: {
  invoices: Document[];
  /** Workspaces with no quote or estimate: list invoices without the Deposit/Balance sequence. */
  plainList?: boolean;
  finalSettled: boolean;
  nextRole: InvoiceRole | null;
  creatingInvoice: boolean;
  firstAccepted?: boolean;
  quoteHasDeposit?: boolean;
  anticipatedBalance?: number | null;
  /** The accepted quote's gross total; the invoices are reconciled against it. */
  agreedQuoteTotal?: number | null;
  selectedId?: string | null;
  scheduleStages?: PresentedScheduleStage[] | null;
  scheduleRawStages?: PaymentScheduleStage[] | null;
  onSelect: (doc: Document) => void;
  onView: (doc: Document) => void;
  onEdit: (doc: Document) => void;
  onExportPdf: (doc: Document) => void;
  onDelete: (doc: Document) => void;
  /** Voids a sent final balance and raises a replacement that can carry deductions. */
  onReissueBalance?: (doc: Document) => void;
  onSaveDeductions?: (doc: Document, deductions: Array<{ name: string; amount: number }>) => Promise<void>;
  /** Opens the credit form, with any expected credit filled in. */
  onCreateCredit?: () => void;
  /** Once the final balance is out, a Credit step is always offered. */
  finalBalanceIssued?: boolean;
  /** Reissued balances by invoice id: their deductions and the voided invoice they replace. */
  balanceAdjustments?: Record<string, BalanceAdjustment>;
  onIssueBalance: () => void;
  /** Recreates the First-accept deposit when it is missing (e.g. deleted after acceptance). */
  onCreateDeposit?: () => void;
  onChangeStatus: (doc: Document, status: DocumentStatus) => void;
  statusBusyId?: string | null;
  onDocumentsChanged: () => void;
}) {
  const { user } = useSupabaseAuth();
  const liveInvoices = useMemo(
    () => invoices.filter((invoice) => String(invoice.status || '').toLowerCase() !== 'void'),
    [invoices],
  );
  const ordered = useMemo(
    () => sortInvoicesForSequence(liveInvoices.map((invoice) => ({ ...invoice, role: invoice.invoiceRole }))),
    [liveInvoices],
  );
  const deposit = ordered.find((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'deposit') || null;
  const laterInvoices = ordered.filter((invoice) => normalizeInvoiceRole(invoice.invoiceRole) !== 'deposit');
  const legacyCombined = !deposit
    && !!firstAccepted
    && laterInvoices.length > 0
    && !!(quoteHasDeposit || laterInvoices[0]?.hasDeposit);
  const depositInvoice = deposit || (legacyCombined ? laterInvoices[0] : null);
  const balanceInvoices = deposit ? laterInvoices : (legacyCombined ? laterInvoices.slice(1) : laterInvoices);
  const depositMissing = !!firstAccepted && !!quoteHasDeposit && liveInvoices.length === 0;

  const [syncById, setSyncById] = useState<Record<string, SyncInfo>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [transmitError, setTransmitError] = useState<string | null>(null);
  const [termsInvoice, setTermsInvoice] = useState<Document | null>(null);
  const [deductionsInvoice, setDeductionsInvoice] = useState<Document | null>(null);
  const [customerSentIds, setCustomerSentIds] = useState<Set<string>>(new Set());

  const refreshFreeAgent = useCallback(async () => {
    const ids = liveInvoices.map((invoice) => invoice.id);
    if (ids.length === 0) {
      setSyncById({});
      return;
    }
    const nextSync: Record<string, SyncInfo> = {};
    await Promise.all(ids.map(async (id) => {
      try {
        nextSync[id] = await getSyncStatus(id);
      } catch {
        nextSync[id] = { syncStatus: 'unsynced' };
      }
    }));
    setSyncById(nextSync);
  }, [liveInvoices]);

  useEffect(() => {
    void refreshFreeAgent();
  }, [refreshFreeAgent]);

  useEffect(() => {
    const ids = liveInvoices.map((invoice) => invoice.id);
    let alive = true;
    if (ids.length === 0) {
      setCustomerSentIds(new Set());
      return;
    }
    void documentIdsWithCustomerTransmissions(ids).then((next) => {
      if (alive) setCustomerSentIds(next);
    });
    return () => { alive = false; };
  }, [liveInvoices]);

  useEffect(() => {
    let alive = true;
    void isAuthenticated().then((value) => {
      if (alive) setSignedIn(value);
    });
    return () => { alive = false; };
  }, []);

  const transmit = async (invoice: Document) => {
    if (normalizeInvoiceRole(invoice.invoiceRole) === 'credit') {
      setTransmitError('Credit invoices are not sent to FreeAgent yet. Raise a credit note there if a refund is needed.');
      return;
    }
    if (signedIn === false) {
      initiateAuthorization();
      return;
    }
    setBusyId(invoice.id);
    setTransmitError(null);
    try {
      const result = await resyncInvoiceToFreeAgent(invoice.id, user?.id, { source: 'invoice_tab' });
      await refreshFreeAgent();
      onDocumentsChanged();
      if (!result.success || result.skipped) {
        setTransmitError(result.validationErrors?.join(' ') || result.error || 'FreeAgent transmit did not complete.');
      }
    } catch (err) {
      setTransmitError(err instanceof Error ? err.message : 'Failed to transmit invoice to FreeAgent.');
    } finally {
      setBusyId(null);
    }
  };

  const steps: SequenceStep[] = [];
  if (plainList) {
    ordered.forEach((invoice, index) => {
      const role = normalizeInvoiceRole(invoice.invoiceRole);
      const title = role === 'works' ? 'Full / works' : invoiceRoleLabel(invoice.invoiceRole);
      steps.push({
        key: invoice.id,
        number: index + 1,
        title,
        icon: stepIcon(title, role),
        body: role === 'credit'
          ? 'Reduces previously invoiced totals.'
          : 'Issue, then send to FreeAgent.',
        invoice,
      });
    });
    if (steps.length === 0) {
      steps.push({
        key: 'none',
        number: 1,
        title: 'Invoices',
        icon: MdReceiptLong,
        body: 'Use Create Invoice to raise one.',
        invoice: null,
      });
    }
  } else if (scheduleStages && scheduleStages.length > 0) {
    scheduleStages.forEach((stage, index) => {
      const invoice = stage.invoiceId
        ? liveInvoices.find((item) => item.id === stage.invoiceId) || null
        : null;
      steps.push({
        key: stage.id,
        number: index + 1,
        title: stage.label,
        icon: stepIcon(stage.label, stage.kind === 'deposit' ? 'deposit' : 'works'),
        body: stage.status === 'issued'
          ? 'Issued and frozen.'
          : stage.status === 'ready'
            ? 'Ready to issue for this milestone.'
            : stage.kind === 'deposit'
              ? 'Created automatically when First Survey is accepted.'
              : 'Waits until the previous milestone is issued and Final has settled.',
        invoice,
        previewAmount: invoice ? undefined : stage.amount,
        previewCaption: invoice ? undefined : stage.amountCaption,
        readyAction: stage.canIssue
          ? {
            label: `Issue ${stage.label.toLowerCase()} invoice`,
            onClick: onIssueBalance,
            disabled: creatingInvoice,
            loading: creatingInvoice,
          }
          : undefined,
      });
    });
    laterInvoices
      .filter((invoice) => {
        const role = normalizeInvoiceRole(invoice.invoiceRole);
        return role === 'variation' || role === 'credit';
      })
      .forEach((invoice) => {
        const role = normalizeInvoiceRole(invoice.invoiceRole);
        steps.push({
          key: invoice.id,
          number: steps.length + 1,
          title: invoiceRoleLabel(invoice.invoiceRole),
          icon: stepIcon(invoiceRoleLabel(invoice.invoiceRole), role),
          body: role === 'credit'
            ? 'Reduces previously invoiced totals.'
            : 'Issue, then send to FreeAgent.',
          invoice,
        });
      });
  } else {
    steps.push({
      key: 'deposit',
      number: 1,
      title: legacyCombined ? 'Deposit (legacy full invoice)' : 'Deposit',
      icon: MdReceiptLong,
      body: deposit
        ? 'Frozen on First accept.'
        : legacyCombined
          ? 'Legacy full-job invoice still standing in for the deposit.'
          : firstAccepted && quoteHasDeposit
            ? 'Deposit was nominated, but no deposit invoice exists yet.'
            : 'Created automatically when First Survey is accepted.',
      invoice: depositInvoice,
      readyAction: depositMissing && onCreateDeposit
        ? {
          label: 'Create deposit invoice',
          onClick: onCreateDeposit,
          disabled: creatingInvoice,
          loading: creatingInvoice,
        }
        : undefined,
    });
    if (balanceInvoices.length === 0) {
      const preview = Number.isFinite(Number(anticipatedBalance)) ? Number(anticipatedBalance) : null;
      steps.push({
        key: 'balance',
        number: 2,
        title: 'Balance',
        icon: MdRequestQuote,
        body: finalSettled
          ? 'Ready to issue after Final Survey.'
          : 'Waits until Final Survey has settled.',
        invoice: null,
        previewAmount: preview,
        previewCaption: preview != null && !finalSettled ? 'Expected' : undefined,
        readyAction: finalSettled && nextRole
          ? {
            label: `Issue ${invoiceRoleLabel(nextRole).toLowerCase()} invoice`,
            onClick: onIssueBalance,
            disabled: creatingInvoice,
            loading: creatingInvoice,
          }
          : undefined,
      });
    } else {
      balanceInvoices.forEach((invoice, index) => {
        const role = normalizeInvoiceRole(invoice.invoiceRole);
        steps.push({
          key: invoice.id,
          number: index + 2,
          title: invoiceRoleLabel(invoice.invoiceRole),
          icon: stepIcon(invoiceRoleLabel(invoice.invoiceRole), role),
          body: role === 'credit'
            ? 'Reduces previously invoiced totals.'
            : 'Issue, then send to FreeAgent.',
          invoice,
        });
      });
      if (finalSettled && nextRole && nextRole !== 'credit') {
        steps.push({
          key: 'next-delta',
          number: steps.length + 1,
          title: invoiceRoleLabel(nextRole),
          icon: stepIcon(invoiceRoleLabel(nextRole), nextRole),
          body: 'Another delta is ready against the settled contract.',
          invoice: null,
          readyAction: {
            label: `Issue ${invoiceRoleLabel(nextRole).toLowerCase()} invoice`,
            onClick: onIssueBalance,
            disabled: creatingInvoice,
            loading: creatingInvoice,
          },
        });
      }
    }
  }

  // A credit is always available once the final balance is out. Any expected credit (the invoices
  // run over the agreed total) is filled in, but the amount is confirmed on the form, never raised blind.
  if (!plainList && finalBalanceIssued && onCreateCredit) {
    const expected = Number(anticipatedBalance) < 0 ? Math.abs(Number(anticipatedBalance)) : 0;
    steps.push({
      key: 'credit',
      number: steps.length + 1,
      title: 'Credit',
      icon: stepIcon('Credit', 'credit'),
      body: expected > 0
        ? 'The invoices run over the agreed total.'
        : 'Reduces previously invoiced totals.',
      invoice: null,
      previewAmount: expected > 0 ? expected : null,
      previewCaption: expected > 0 ? 'Expected' : undefined,
      readyAction: {
        label: expected > 0 ? 'Issue credit invoice' : 'Create credit',
        onClick: onCreateCredit,
      },
    });
  }

  return (
    <Stack gap={1.5}>
      {signedIn === false && (
        <Alert color="warning">
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Typography level="body-sm">Sign in to FreeAgent to transmit invoices from this tab.</Typography>
            <Button size="sm" onClick={() => initiateAuthorization()}>Connect FreeAgent</Button>
          </Stack>
        </Alert>
      )}
      {transmitError && <Alert color="danger">{transmitError}</Alert>}
      <Stack gap={0}>
        {steps.map((step, index) => {
          const invoice = step.invoice;
          const sync = invoice ? syncById[invoice.id] : undefined;
          const selected = !!(invoice && selectedId === invoice.id);
          const role = invoice ? normalizeInvoiceRole(invoice.invoiceRole) : null;
          const status = invoice ? invoiceStatusMeta(invoice, customerSentIds.has(invoice.id)) : null;
          const adjustment = invoice ? balanceAdjustments[invoice.id] || null : null;
          const deductionsEditable = !!adjustment
            && String(invoice?.status || '').toLowerCase() === 'draft'
            && !sync?.freeAgentUrl;
          const rowMuted = selected ? 'var(--jarvis-selection-text)' : 'neutral.500';
          const schedule = invoice && invoicePaymentTermsEditable(invoice.invoiceRole)
            ? resolveInvoicePaymentSchedule({
              invoiceRole: invoice.invoiceRole,
              issuedAt: invoice.issuedAt,
              createdAt: invoice.createdAt,
              paymentTermsInDays: invoice.paymentTermsInDays,
              locked: invoicePaymentScheduleLocked({ freeAgentUrl: sync?.freeAgentUrl }),
            })
            : null;
          const freeAgentCaption = sync?.syncStatus === 'synced'
            ? 'In FreeAgent'
            : sync?.syncStatus === 'error'
              ? 'FreeAgent error'
              : '';
          const statusCaption = [
            schedule ? `${schedule.label} · Due ${formatDateGb(schedule.dueDate)}` : '',
            freeAgentCaption || (schedule ? '' : sync?.syncError || ''),
          ].filter(Boolean).join(' · ');
          return (
            <Box key={step.key} sx={{ display: 'flex', gap: 1.5, minHeight: 88 }}>
              <Box
                sx={{
                  width: 44,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pt: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 'md',
                    bgcolor: 'var(--jarvis-card-icon-bg)',
                    color: 'var(--jarvis-card-icon-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <IconWrapper icon={step.icon} />
                </Box>
                {index < steps.length - 1 && (
                  <Box sx={{ width: 2, flex: 1, bgcolor: 'primary.200', my: 0.5, borderRadius: 1 }} />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                  gap: 1.5,
                  py: 1.25,
                  minHeight: 108,
                  borderBottom: index < steps.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 'auto', sm: 110, md: 170 },
                    pt: { xs: 0.5, sm: 0 },
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography level="title-sm">{step.title}</Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {step.body}
                  </Typography>
                </Box>

                {invoice ? (
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={selected}
                    onClick={() => onSelect(invoice)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect(invoice);
                      }
                    }}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr) auto',
                        sm: 'minmax(140px, 200px) minmax(0, 1fr) auto auto',
                      },
                      gridTemplateAreas: {
                        xs: '"ref amount" "status menu"',
                        sm: '"ref status amount menu"',
                      },
                      columnGap: { xs: 1.5, sm: 2.5 },
                      rowGap: 0.75,
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 'sm',
                      px: 1.25,
                      py: 1,
                      flex: 1,
                      minWidth: 0,
                      bgcolor: selected ? 'var(--jarvis-selection-bg)' : 'transparent',
                      color: selected ? 'var(--jarvis-selection-text)' : 'inherit',
                    }}
                  >
                    <Box sx={{ gridArea: 'ref', minWidth: 0 }}>
                      <Typography level="body-sm" fontWeight="lg" sx={{ color: 'inherit' }}>
                        {documentDisplayRef(invoice)}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: rowMuted, opacity: selected ? 0.85 : 1 }} noWrap>
                        {invoice.title}
                      </Typography>
                      {adjustment && (
                        <Typography level="body-xs" sx={{ color: rowMuted, opacity: selected ? 0.85 : 1 }}>
                          {[
                            adjustment.replacesRef ? `Replaces ${adjustment.replacesRef} (void)` : 'Reissued balance',
                            ...adjustment.deductions.map((item) => `${item.name} −${formatMoney(item.amount)}`),
                          ].join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{ gridArea: 'status', minWidth: 0 }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Dropdown>
                        <MenuButton
                          slots={{ root: Chip }}
                          slotProps={{
                            root: {
                              size: 'sm',
                              variant: 'soft',
                              color: status?.color,
                              disabled: statusBusyId === invoice.id,
                              startDecorator: status?.icon ? <IconWrapper icon={status.icon} /> : undefined,
                              sx: { cursor: 'pointer', maxWidth: '100%' },
                              'aria-label': `Change status (${status?.label || 'Draft'})`,
                            },
                          }}
                        >
                          {status?.label}
                        </MenuButton>
                        <Menu placement="bottom-start" size="sm">
                          {getAvailableStatuses('Invoice').map((nextStatus) => (
                            <MenuItem
                              key={nextStatus}
                              selected={String(invoice.status || '').trim().toLowerCase() === nextStatus}
                              disabled={statusBusyId === invoice.id}
                              onClick={() => onChangeStatus(invoice, nextStatus)}
                            >
                              {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Dropdown>
                      {statusCaption && (
                        <Typography level="body-xs" sx={{ color: rowMuted, opacity: selected ? 0.85 : 1, mt: 0.35 }}>
                          {statusCaption}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ gridArea: 'amount', textAlign: 'right', flexShrink: 0, minWidth: 104 }}>
                      <Typography level="title-sm" sx={{ color: 'inherit' }}>
                        {formatMoney(invoice.grossTotal)}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: rowMuted, opacity: selected ? 0.85 : 1 }}>
                        Invoice
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      gap={0.5}
                      alignItems="center"
                      sx={{ gridArea: 'menu', color: 'inherit' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Dropdown>
                        <MenuButton
                          slots={{ root: IconButton }}
                          slotProps={{
                            root: {
                              size: 'sm',
                              variant: 'plain',
                              'aria-label': `${step.title} actions`,
                              sx: selected ? { color: 'var(--jarvis-selection-text)' } : undefined,
                            },
                          }}
                        >
                          <IconWrapper icon={FaEllipsisV} />
                        </MenuButton>
                        <Menu placement="bottom-end">
                          <MenuItem onClick={() => onView(invoice)}>View</MenuItem>
                          <MenuItem onClick={() => onEdit(invoice)}>Edit</MenuItem>
                          <MenuItem onClick={() => onExportPdf(invoice)}>Export PDF</MenuItem>
                          {invoicePaymentTermsEditable(invoice.invoiceRole)
                            && !invoicePaymentScheduleLocked({ freeAgentUrl: sync?.freeAgentUrl }) && (
                            <MenuItem onClick={() => setTermsInvoice(invoice)}>Edit payment terms</MenuItem>
                          )}
                          {role !== 'credit' && (
                            <MenuItem
                              disabled={busyId === invoice.id}
                              onClick={() => { void transmit(invoice); }}
                            >
                              {sync?.syncStatus === 'synced' ? 'Resend to FreeAgent' : 'Send to FreeAgent'}
                            </MenuItem>
                          )}
                          {deductionsEditable && onSaveDeductions && (
                            <MenuItem onClick={() => setDeductionsInvoice(invoice)}>Edit deductions</MenuItem>
                          )}
                          {onReissueBalance && balanceReissueAllowed({
                            invoice,
                            stages: scheduleRawStages,
                            customerTransmitted: customerSentIds.has(invoice.id),
                          }) && (
                            <MenuItem onClick={() => onReissueBalance(invoice)}>Reissue balance</MenuItem>
                          )}
                          {sync?.freeAgentUrl && (
                            <MenuItem
                              component="a"
                              href={sync.freeAgentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View in FreeAgent
                            </MenuItem>
                          )}
                          {invoiceDeleteAllowedWithSchedule({
                            invoice,
                            invoices: liveInvoices,
                            stages: scheduleRawStages,
                          }) && (
                            <MenuItem color="danger" onClick={() => onDelete(invoice)}>Delete invoice</MenuItem>
                          )}
                        </Menu>
                      </Dropdown>
                    </Stack>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: { xs: 0, sm: 180 },
                        minHeight: 76,
                        border: '1px dashed',
                        borderColor: 'neutral.300',
                        borderRadius: 'md',
                        px: 1.5,
                        py: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography level="body-sm" fontWeight="lg">
                        {step.key === 'credit' && step.previewAmount == null ? 'No credit expected' : 'No invoice yet'}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {step.previewAmount != null
                          ? `${formatMoney(step.previewAmount)}${step.previewCaption ? ` · ${step.previewCaption}` : ''}`
                          : step.body}
                      </Typography>
                    </Box>
                    {step.readyAction && (
                      <Button
                        size="sm"
                        disabled={step.readyAction.disabled}
                        loading={step.readyAction.loading}
                        startDecorator={<IconWrapper icon={FaPlus} />}
                        onClick={step.readyAction.onClick}
                        sx={{
                          flexShrink: 0,
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          minWidth: 152,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.readyAction.label}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
      {Number(agreedQuoteTotal) > 0 && (
        <InvoiceReconciliationSummary
          reconciliation={reconcileInvoices({
            quoteTotal: Number(agreedQuoteTotal) + totalBalanceDeductions(balanceAdjustments),
            deductions: totalBalanceDeductions(balanceAdjustments),
            invoices: invoices.map((invoice) => ({
              id: invoice.id,
              role: invoice.invoiceRole,
              status: invoice.status,
              grossTotal: invoice.grossTotal,
              invoiceRef: invoice.invoiceRef,
              sourceSurveyEvent: invoice.sourceSurveyEvent,
            })),
          })}
        />
      )}
      {deductionsInvoice && onSaveDeductions && (
        <BalanceDeductionsModal
          open
          invoiceRef={documentDisplayRef(deductionsInvoice)}
          balanceBeforeDeductions={Number(deductionsInvoice.grossTotal || 0)
            + (balanceAdjustments[deductionsInvoice.id]?.deductions || []).reduce((sum, item) => sum + item.amount, 0)}
          deductions={balanceAdjustments[deductionsInvoice.id]?.deductions || []}
          onClose={() => setDeductionsInvoice(null)}
          onSave={(deductions) => onSaveDeductions(deductionsInvoice, deductions)}
        />
      )}
      <InvoicePaymentTermsModal
        open={Boolean(termsInvoice && invoicePaymentTermsEditable(termsInvoice.invoiceRole))}
        locked={invoicePaymentScheduleLocked({
          freeAgentUrl: termsInvoice ? syncById[termsInvoice.id]?.freeAgentUrl : null,
        })}
        invoiceRole={termsInvoice?.invoiceRole}
        issuedAt={termsInvoice?.issuedAt}
        createdAt={termsInvoice?.createdAt}
        paymentTermsInDays={termsInvoice?.paymentTermsInDays}
        onClose={() => setTermsInvoice(null)}
        onSave={async ({ invoiceDate, paymentTermsInDays }) => {
          if (!termsInvoice) return;
          await documentsService.updateDocument(termsInvoice.id, {
            issuedAt: dateOnlyToIssuedAt(invoiceDate),
            paymentTermsInDays,
          });
          onDocumentsChanged();
        }}
      />
    </Stack>
  );
}

const RECONCILIATION_TONE = {
  complete: { color: 'success', label: 'Fully invoiced' },
  open: { color: 'primary', label: 'Still to invoice' },
  short: { color: 'warning', label: 'Short of the quote' },
  over: { color: 'danger', label: 'Over the quote' },
} as const;

/** Proves the invoices add up to the accepted quote (plus variations, less credits). */
function InvoiceReconciliationSummary({ reconciliation }: { reconciliation: InvoiceReconciliation }) {
  const { agreedTotal, invoiced, remaining, parts, state, variations, credits, deductions, quoteTotal } = reconciliation;
  const tone = RECONCILIATION_TONE[state];
  const progress = agreedTotal > 0 ? Math.min(100, Math.max(0, (invoiced / agreedTotal) * 100)) : 0;
  const breakdown = parts.map((part) => `${part.label} ${formatMoney(part.amount)}`).join(' + ').replace(/\+ -/g, '− ');
  const adjustments = [
    variations ? `+ variations ${formatMoney(variations)}` : '',
    credits ? `− credits ${formatMoney(credits)}` : '',
    deductions ? `− deductions ${formatMoney(deductions)}` : '',
  ].filter(Boolean).join(' ');

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'neutral.outlinedBorder',
        borderRadius: 'md',
        px: 2,
        py: 1.5,
        ml: { sm: 7 },
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 1, md: 3 }} alignItems={{ md: 'center' }}>
        <Stack direction="row" gap={3} flexWrap="wrap" sx={{ flex: 1 }}>
          <Box>
            <Typography level="body-xs" textColor="neutral.500">Agreed</Typography>
            <Typography level="title-sm">{formatMoney(agreedTotal)}</Typography>
            {adjustments && (
              <Typography level="body-xs" textColor="neutral.500">Quote {formatMoney(quoteTotal)} {adjustments}</Typography>
            )}
          </Box>
          <Box>
            <Typography level="body-xs" textColor="neutral.500">Invoiced</Typography>
            <Typography level="title-sm">{formatMoney(invoiced)}</Typography>
          </Box>
          <Box>
            <Typography level="body-xs" textColor="neutral.500">{remaining < 0 ? 'Over by' : 'Remaining'}</Typography>
            <Typography level="title-sm" color={state === 'complete' ? undefined : tone.color}>
              {formatMoney(Math.abs(remaining))}
            </Typography>
          </Box>
        </Stack>
        <Chip size="sm" variant="soft" color={tone.color} startDecorator={state === 'complete' ? <IconWrapper icon={FaCheck} /> : undefined}>
          {tone.label}
        </Chip>
      </Stack>
      <LinearProgress determinate value={progress} color={tone.color} thickness={4} sx={{ mt: 1.25 }} />
      {breakdown && (
        <Typography level="body-xs" textColor="neutral.500" sx={{ mt: 0.75 }}>
          {breakdown} = {formatMoney(invoiced)}
        </Typography>
      )}
    </Box>
  );
}
